import React, { useEffect, useState } from "react";
import BookDistributionSection from "./sections/BookDistributionSection";
import AccommodationSection from "./sections/AccommodationSection";
import MediaAdviceSection from "./sections/MediaAdviceSection";
import PublicResponseSection from "./sections/PublicResponseSection";

// The four sections of a place, in the journey page's order.
export const PLACE_TABS = Object.freeze([
  { key: "books", label: "Book Distribution" },
  { key: "accommodation", label: "Accommodation" },
  { key: "mediaAdvice", label: "Media & Advice" },
  { key: "response", label: "Public Response" },
]);

// A place's section tabs and their panels — the same on Add Journey, Edit
// Journey (inside a place card) and Update Journey (under a stop of the
// timeline). A tab opens its section: its saved records, and its form for
// adding or editing them.
//
// `usePlaceStore(place, load)` supplies the place's records and actions (staged
// on Add Journey, live through the API elsewhere) — the same function for the
// component's whole life; `load` says which sections have been opened, so a
// section's records are read the first time its tab is. A section, once
// opened, stays mounted (hidden when another tab is chosen) so what was typed
// in its form is not lost by switching tabs.
//
// `perms.tabs` lists the tabs to show; the other `perms` say what each section
// may record. `activeTab` / `onTabChange(key | null)`: clicking the open tab
// closes it.
export default function JourneyPlaceSections({
  place,
  idBase,
  title,
  activeTab,
  onTabChange,
  usePlaceStore,
  perms,
  range,
  bookCatalog,
  tabErrors = {},
  disabled = false,
}) {
  const [opened, setOpened] = useState(() => new Set(activeTab ? [activeTab] : []));
  useEffect(() => {
    if (activeTab) {
      setOpened((current) => (current.has(activeTab) ? current : new Set([...current, activeTab])));
    }
  }, [activeTab]);
  const store = usePlaceStore(place, {
    books: opened.has("books"),
    stays: opened.has("accommodation"),
    media: opened.has("mediaAdvice"),
    advice: opened.has("mediaAdvice"),
    responses: opened.has("response"),
  });
  const tabs = PLACE_TABS.filter((tab) => perms.tabs.includes(tab.key));

  const openTab = (key) => {
    setOpened((current) => (current.has(key) ? current : new Set([...current, key])));
    onTabChange(key);
  };

  const sectionFor = (key) => {
    const common = { idPrefix: `${idBase}-${key}`, place, store, disabled };
    switch (key) {
      case "books":
        return <BookDistributionSection {...common} bookCatalog={bookCatalog} canManage={perms.books} />;
      case "accommodation":
        return <AccommodationSection {...common} canManage={perms.accommodation} />;
      case "mediaAdvice":
        return (
          <MediaAdviceSection
            {...common}
            showMedia={perms.mediaVisible}
            showAdvice={perms.adviceVisible}
            canUpload={perms.media}
            canAdvise={perms.advice}
          />
        );
      case "response":
        return <PublicResponseSection {...common} canManage={perms.response} />;
      default:
        return null;
    }
  };

  return (
    <>
      <div className="tsp-pc-tabs" role="tablist" aria-label={`Details of ${title}`}>
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            id={`${idBase}-tab-${tab.key}`}
            aria-selected={activeTab === tab.key}
            aria-controls={`${idBase}-panel-${tab.key}`}
            className={`tsp-tab${activeTab === tab.key ? " active" : ""}${tabErrors[tab.key] ? " has-error" : ""}`}
            onClick={() => openTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {tabs
        .filter((tab) => opened.has(tab.key))
        .map((tab) => (
          <div key={tab.key} role="tabpanel" id={`${idBase}-panel-${tab.key}`} aria-labelledby={`${idBase}-tab-${tab.key}`} hidden={activeTab !== tab.key} className="tsp-pc-panel">
            {tabErrors[tab.key] && (
              <p className="tsp-form-error" role="alert">
                {tabErrors[tab.key]}
              </p>
            )}
            {sectionFor(tab.key)}
          </div>
        ))}
      {!activeTab && <p className="tsp-muted-text tsp-pc-hint">Choose a section above to add or edit this place&apos;s details.</p>}
    </>
  );
}
